import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getJson } from "../../lib/http";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useTruckYardStore } from "../../store/truckYardStore";
import type { Yard } from "../../pages/patios-maniobras/yardsSection/types";
import { YARD_METRICS_POLL_MS } from "./useYardMetrics";

interface YardsListBody {
  data?: Yard[];
  meta?: { totalItems?: number };
}

/**
 * Refresco silencioso del listado de patios.
 *
 * Existe por la salud del controlador. `yard.health` no es un campo del
 * documento: la calcula el backend en cada `GET /truck-yard/yards` sobre la
 * página que devuelve, deliberadamente embebida en el listado y no en un
 * endpoint aparte (truckYardController.js:229-234). La consecuencia es que la
 * única forma de que el punto de color se mantenga vivo es volver a pedir el
 * listado — y el criterio de #499 es que la tarjeta se refresque sola: un punto
 * que dice "En vivo" porque así estaba cuando alguien abrió la pestaña hace tres
 * horas es el mismo defecto de `yard.status` con otro origen.
 *
 * NO se reusa `fetchYards()` del store: ese pone `loading: true`, y la pantalla
 * cambia la lista entera por "Cargando patios…" mientras dure. Cada 30 s. La
 * escritura directa al store es el mismo patrón que ya usa
 * `useAllTruckYardVehicles`, y el `initialData` + `staleTime` son los de
 * accessControlSection: evitan un segundo request en el montaje —de eso ya se
 * encarga el `fetchYards()` de la página— y hacen que el primero ocurra recién
 * a los 30 s.
 */
export function useYardsPoll({ polling = true }: { polling?: boolean } = {}) {
  const companyId = useCompaniesStore((s) => s.companyId);

  const query = useQuery({
    queryKey: ["truckYards", "list", companyId],
    queryFn: ({ signal }) =>
      getJson<YardsListBody>("/truck-yard/yards", undefined, { signal }),
    // Mismo pedido que hace la página al montar (sin params: el backend pagina
    // de a 20). Si algún día la página pide otra cosa, tienen que cambiar las
    // dos o el refresco reemplazaría la lista por otra distinta.
    initialData: () => ({ data: useTruckYardStore.getState().yards }) as YardsListBody,
    staleTime: YARD_METRICS_POLL_MS,
    // `polling` apaga sólo el refresco periódico (ver useYardMetrics): la lista
    // inicial la trae el `fetchYards()` de la página, así que acá no hay carga
    // inicial que perder, pero el criterio se mantiene igual en los dos hooks.
    refetchInterval: polling ? YARD_METRICS_POLL_MS : (false as const),
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    // `isFetched` es la guarda que impide que el `initialData` —que puede ser
    // el arreglo vacío del primer render— se escriba de vuelta encima de lo que
    // el fetch inicial de la página acaba de traer.
    if (query.isFetched && query.data?.data) {
      useTruckYardStore.setState({
        yards: query.data.data,
        totalYards: query.data.meta?.totalItems ?? query.data.data.length,
      });
    }
  }, [query.isFetched, query.data]);

  return query;
}
